import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { SectionHeader, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { TID } from "../constants/testIds";
import { UserPlus, X, Search, Users, Upload } from "lucide-react";

const ROLES = [
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR" },
  { value: "company_admin", label: "Company Admin" },
];

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    email: "", name: "", password: "", role: "employee", department: "", title: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users", { params: q ? { q } : {} });
      setUsers(data);
    } catch (e) { toast.error("Failed to load users"); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/users", form);
      toast.success(`Invited ${form.name}`);
      setCreating(false);
      setForm({ email: "", name: "", password: "", role: "employee", department: "", title: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) { toast.error("Failed to update"); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <SectionHeader
        title="Users"
        subtitle="Add employees, HR handlers, managers, and other admins to your workspace."
        right={
          <div className="flex items-center gap-2">
            <Link to="/admin/users/import" className="btn-ghost" data-testid="users-bulk-import-link">
              <Upload size={14} strokeWidth={2}/> Bulk import
            </Link>
            <button className="btn-primary" onClick={() => setCreating(true)} data-testid={TID.createUserBtn}>
              <UserPlus size={16}/> Add user
            </button>
          </div>
        }
      />

      <div className="data-cell p-4 mb-4 flex items-center gap-3">
        <Search size={16} className="text-slate-400" strokeWidth={2}/>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="flex-1 border-none focus:outline-none text-sm"
          data-testid="users-search"
        />
        <span className="text-xs font-mono text-slate-400">{users.length} accounts</span>
      </div>

      {users.length === 0 ? (
        <EmptyState icon={Users} title="No users yet" subtitle="Add your first user to get started." />
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow">Name</th>
                <th className="px-4 py-3 eyebrow">Email</th>
                <th className="px-4 py-3 eyebrow">Role</th>
                <th className="px-4 py-3 eyebrow">Department</th>
                <th className="px-4 py-3 eyebrow">Title</th>
                <th className="px-4 py-3 eyebrow">Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="row-hover border-b border-slate-100 last:border-b-0" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3"><span className="pill pill-closed">{u.role.replaceAll("_"," ")}</span></td>
                  <td className="px-4 py-3 text-slate-600">{u.department || ","}</td>
                  <td className="px-4 py-3 text-slate-600">{u.title || ","}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(u)}
                      className={"pill " + (u.is_active ? "pill-resolved" : "pill-closed")}
                    >
                      {u.is_active ? "active" : "inactive"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create user modal */}
      {creating && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white border border-slate-300 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-semibold">Add new user</h3>
              <button type="button" onClick={() => setCreating(false)}><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="eyebrow block mb-1">Name</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border border-slate-300 px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="eyebrow block mb-1">Email</label>
                <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full border border-slate-300 px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="eyebrow block mb-1">Temporary password</label>
                <input required minLength={8} type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full border border-slate-300 px-3 py-2 text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-1">Role</label>
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white">
                    {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-1">Department</label>
                  <input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="w-full border border-slate-300 px-3 py-2 text-sm"/>
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-1">Job title</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full border border-slate-300 px-3 py-2 text-sm"/>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting} data-testid={TID.createUserSubmit}>
                {submitting ? "Adding…" : "Add user"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
